import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

// Créer le contexte
const AuthContext = createContext({});

// Hook pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé dans un AuthProvider");
  }
  return context;
};

// Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [entreprise, setEntreprise] = useState(null);
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier la session au chargement
  useEffect(() => {
    // Récupérer la session actuelle
    const getSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) throw error;

        if (session?.user) {
          setUser(session.user);
          await loadUserData(session.user.id);
        }
      } catch (err) {
        console.error("Erreur session:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Écouter les changements d'authentification
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth event:", event);

      if (session?.user) {
        setUser(session.user);
        await loadUserData(session.user.id);
      } else {
        setUser(null);
        setEntreprise(null);
        setProfil(null);
      }
      setLoading(false);
    });

    // Cleanup
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Charger le profil et l'organisation de l'utilisateur
  const loadUserData = async (userId) => {
    try {
      // 1. Chercher le profil de l'utilisateur
      const { data: profilData, error: profilError } = await supabase
        .from("profils")
        .select("*")
        .eq("id", userId)
        .single();

      if (profilError && profilError.code !== "PGRST116") {
        console.error("Erreur chargement profil:", profilError);
      }

      if (profilData) {
        setProfil(profilData);

        // 2. Si le profil a une organisation_id, charger l'organisation
        if (profilData.organisation_id) {
          const { data: orgData, error: orgError } = await supabase
            .from("organisations")
            .select("*")
            .eq("id", profilData.organisation_id)
            .single();

          if (orgError && orgError.code !== "PGRST116") {
            console.error("Erreur chargement organisation:", orgError);
          }

          if (orgData) {
            setEntreprise(orgData);
            return;
          }
        }
      }

      // 3. Fallback: chercher une organisation créée par cet utilisateur
      const { data: orgByCreator, error: orgByCreatorError } = await supabase
        .from("organisations")
        .select("*")
        .eq("created_by", userId)
        .limit(1)
        .single();

      if (!orgByCreatorError && orgByCreator) {
        setEntreprise(orgByCreator);

        // Mettre à jour le profil si nécessaire
        if (profilData && !profilData.organisation_id) {
          await supabase
            .from("profils")
            .update({ organisation_id: orgByCreator.id })
            .eq("id", userId);
        }
      }
    } catch (err) {
      console.error("Erreur chargement données utilisateur:", err);
    }
  };

  // Inscription
  const signUp = async (email, password, nomEntreprise) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Créer le compte utilisateur
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) throw authError;

      // 2. Créer l'organisation liée à l'utilisateur
      if (authData.user) {
        const { data: orgData, error: orgError } = await supabase
          .from("organisations")
          .insert([
            {
              nom: nomEntreprise,
              created_by: authData.user.id,
              plan_actif: "essai",
            },
          ])
          .select()
          .single();

        if (orgError) throw orgError;

        // 3. Créer le profil utilisateur
        const { error: profilError } = await supabase.from("profils").insert([
          {
            id: authData.user.id,
            organisation_id: orgData.id,
            email: email,
            role: "admin",
          },
        ]);

        if (profilError) {
          console.error("Erreur création profil:", profilError);
        }

        setEntreprise(orgData);
      }

      return { success: true, user: authData.user };
    } catch (err) {
      console.error("Erreur inscription:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Connexion
  const signIn = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { success: true, user: data.user };
    } catch (err) {
      console.error("Erreur connexion:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setEntreprise(null);
      setProfil(null);
    } catch (err) {
      console.error("Erreur déconnexion:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le mot de passe
  const resetPassword = async (email) => {
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      return { success: true };
    } catch (err) {
      console.error("Erreur reset password:", err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Créer une organisation (si l'utilisateur n'en a pas)
  const createEntreprise = async (nomEntreprise, secteur = "", taille = "") => {
    if (!user) return { success: false, error: "Non connecté" };

    try {
      // 1. Créer l'organisation
      const { data: orgData, error: orgError } = await supabase
        .from("organisations")
        .insert([
          {
            nom: nomEntreprise,
            secteur,
            nombre_employes: taille,
            created_by: user.id,
            plan_actif: "essai",
          },
        ])
        .select()
        .single();

      if (orgError) throw orgError;

      // 2. Mettre à jour ou créer le profil
      const { error: profilError } = await supabase.from("profils").upsert({
        id: user.id,
        organisation_id: orgData.id,
        email: user.email,
        role: "admin",
      });

      if (profilError) {
        console.error("Erreur mise à jour profil:", profilError);
      }

      setEntreprise(orgData);
      return { success: true, entreprise: orgData };
    } catch (err) {
      console.error("Erreur création organisation:", err);
      return { success: false, error: err.message };
    }
  };

  // Rafraîchir les données utilisateur
  const refreshUserData = async () => {
    if (user) {
      await loadUserData(user.id);
    }
  };

  const value = {
    user,
    entreprise,
    profil,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    createEntreprise,
    refreshUserData,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
